import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Search, Check, Loader2 } from 'lucide-react';
import axiosInstance from '../../../api/axios';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { extractRisksArray } from '../../../components/risks-table/utils.js';

/**
 * ControlWordingEnrichModal
 *
 * Risk-driven AI enrichment of the Control Wording field.
 *   - Search & select one or more Risks (from /api/risks); each shows its
 *     cycle + process (from process_mappings).
 *   - Add free-text context.
 *   - Generate → POST /api/controls/enrich-wording (SOX guidelines + guardrails
 *     + content filter applied server-side).
 *   - Before/after review → Accept populates the editable field (parent saves
 *     with the normal Save button).
 *
 * Props:
 *   control        — control data object (id, title, frequency, type, objective)
 *   currentWording — current control wording text
 *   onAccept       — (improvedWording: string) => void
 *   onClose        — () => void
 */
export default function ControlWordingEnrichModal({ control, currentWording, onAccept, onClose }) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [selected, setSelected]     = useState([]);   // selected risk objects
  const [extra, setExtra]           = useState('');
  const [phase, setPhase]           = useState('input'); // input | generating | review
  const [improved, setImproved]     = useState('');
  const [error, setError]           = useState('');
  // Cycle / process filters (multi-select)
  const [cycles, setCycles]           = useState([]);   // string[] of cycle names — from /api/getUniqueSOXCycles
  const [processes, setProcesses]     = useState([]);   // {value(id), name, cycleId, cycleName}
  const [selectedCycles, setSelectedCycles]       = useState([]);   // cycle names
  const [selectedProcesses, setSelectedProcesses] = useState([]);   // {value, name, cycleName}
  const debounceRef = useRef(null);

  /* ---- Load cycles directly from the dedicated endpoint ---- */
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get('/api/getUniqueSOXCycles');
        const raw = resp.data?.cycles;
        if (Array.isArray(raw)) {
          setCycles(
            raw
              .map(c => String(c?.name ?? c?.cycle_id ?? ''))
              .filter(Boolean)
          );
        }
      } catch (e) { console.error('[EnrichModal] failed to load cycles', e); }
    })();
  }, []);

  /* ---- Load processes (for process filter) ---- */
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get('/api/processes', { params: { page_size: 100 } });
        const body = resp.data ?? {};
        const root = body?.data && typeof body.data === 'object' ? body.data : body;
        const list = Array.isArray(root?.processes) ? root.processes
          : Array.isArray(root?.items) ? root.items
          : Array.isArray(root) ? root : [];
        setProcesses(list.map(p => ({
          value:     String(p?.id ?? p?.process_id ?? ''),
          name:      String(p?.name ?? p?.process_name ?? p?.process_id ?? ''),
          cycleId:   String(p?.cycle_id ?? ''),
          cycleName: String(p?.sox_cycle_name ?? p?.cycle_name ?? p?.cycle_id ?? ''),
        })).filter(p => p.value));
      } catch (e) { console.error('[EnrichModal] failed to load processes', e); }
    })();
  }, []);

  // Processes filtered by selected cycles (uses sox_cycle_name matching)
  const filteredProcesses = selectedCycles.length
    ? processes.filter(p => selectedCycles.includes(p.cycleName))
    : processes;

  // Process ids the search should be confined to (selected processes, else all
  // processes within selected cycles, else none = search across everything).
  const targetProcessIds = selectedProcesses.length
    ? selectedProcesses.map(p => p.value)
    : selectedCycles.length
    ? processes.filter(p => selectedCycles.includes(p.cycleName)).map(p => p.value)
    : [];

  const toggleCycle = (name) =>
    setSelectedCycles(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
  const toggleProcess = (p) =>
    setSelectedProcesses(prev => prev.find(x => x.value === p.value)
      ? prev.filter(x => x.value !== p.value) : [...prev, p]);

  /* ---- Risk search (debounced; confined to selected cycles/processes) ---- */
  const targetKey = targetProcessIds.join(',');
  useEffect(() => {
    if (!query.trim() && targetProcessIds.length === 0) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const search = query.trim();
        let merged = [];
        if (targetProcessIds.length > 0) {
          // One request per selected process; merge + dedupe by risk id.
          const responses = await Promise.all(
            targetProcessIds.slice(0, 25).map(pid =>
              axiosInstance.get('/api/risks', {
                params: { page_size: 20, process_id: pid, ...(search ? { search } : {}) },
              }).then(r => extractRisksArray(r.data)).catch(() => [])
            )
          );
          const seen = new Set();
          responses.flat().forEach(r => {
            const k = r.id ?? r.risk_id ?? r.riskId;
            if (!seen.has(k)) { seen.add(k); merged.push(r); }
          });
        } else {
          const resp = await axiosInstance.get('/api/risks', { params: { page_size: 20, search } });
          merged = extractRisksArray(resp.data);
        }
        setResults(merged);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, targetKey]);

  /* ---- Risk → cycle/process display helpers ---- */
  const riskCycleProcess = (r) => {
    const m = r.process_mappings || r.processMappings || [];
    if (!m.length) return null;
    const parts = m.slice(0, 2).map(pm =>
      [pm.cycle_name || pm.cycleName, pm.process_name || pm.processName].filter(Boolean).join(' / ')
    ).filter(Boolean);
    return parts.join('; ');
  };
  const riskDesc  = (r) => r.risk_description || r.riskDescription || r.description || '(no description)';
  const riskLevel = (r) => r.risk_level || r.riskLevel || '';
  const riskKey   = (r) => r.id ?? r.risk_id ?? r.riskId;

  const toggleRisk = (r) => {
    setSelected(prev =>
      prev.find(x => riskKey(x) === riskKey(r))
        ? prev.filter(x => riskKey(x) !== riskKey(r))
        : [...prev, r]
    );
  };

  /* ---- Generate ---- */
  const handleGenerate = async () => {
    setError('');
    setPhase('generating');
    try {
      const risksPayload = selected.map(r => {
        const m = (r.process_mappings || r.processMappings || [])[0] || {};
        return {
          risk_description: riskDesc(r),
          risk_level:       riskLevel(r),
          risk_category:    r.risk_category || r.riskCategory || '',
          cycle_name:       m.cycle_name || m.cycleName || '',
          process_name:     m.process_name || m.processName || '',
        };
      });
      const resp = await axiosInstance.post('/api/controls/enrich-wording', {
        control: {
          control_id:   control?.control_id,
          title:        control?.title,
          frequency:    control?.frequency,
          control_type: control?.control_type,
          objective:    control?.objective,
        },
        current_wording: currentWording || '',
        risks:           risksPayload,
        extra_context:   extra,
        scope: {
          cycles:    selectedCycles,
          processes: selectedProcesses.map(p => p.name),
        },
      });
      if (resp.data?.error) throw new Error(resp.data.error);
      setImproved(resp.data?.improved_wording || '');
      setPhase('review');
    } catch (err) {
      setError(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Enrichment failed'));
      setPhase('input');
    }
  };

  const canGenerate = (currentWording || '').trim() || selected.length > 0 || extra.trim();

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">Enrich Control Wording with AI</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          {phase !== 'review' && (
            <>
              {/* Current wording */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Current Wording</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
                  {currentWording || <span className="italic text-gray-400">(empty)</span>}
                </p>
              </div>

              {/* Risk search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Add Risk(s)</label>

                {/* Cycle / Process filters (multi-select) */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select
                    value=""
                    onChange={e => { if (e.target.value) toggleCycle(e.target.value); }}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">+ Add Cycle (all if none)</option>
                    {cycles.filter(c => !selectedCycles.includes(c)).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value=""
                    onChange={e => {
                      const p = filteredProcesses.find(x => x.value === e.target.value);
                      if (p) toggleProcess(p);
                    }}
                    className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">+ Add Process (all if none)</option>
                    {filteredProcesses.filter(p => !selectedProcesses.find(x => x.value === p.value)).map(p => (
                      <option key={p.value} value={p.value}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Selected cycle/process chips */}
                {(selectedCycles.length > 0 || selectedProcesses.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {selectedCycles.map(c => (
                      <span key={`c-${c}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">
                        Cycle: {c}
                        <button type="button" onClick={() => toggleCycle(c)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                    {selectedProcesses.map(p => (
                      <span key={`p-${p.value}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                        {p.name}
                        <button type="button" onClick={() => toggleProcess(p)} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text" value={query} onChange={e => setQuery(e.target.value)}
                    placeholder={targetProcessIds.length ? 'Search within selected cycles/processes…' : 'Search risks by description or ID…'}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  {searching && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
                </div>

                {/* Results */}
                {results.length > 0 && (
                  <div className="mt-2 max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-50">
                    {results.map(r => {
                      const cp = riskCycleProcess(r);
                      const isSel = !!selected.find(x => riskKey(x) === riskKey(r));
                      return (
                        <button
                          type="button"
                          key={riskKey(r)}
                          onClick={() => toggleRisk(r)}
                          className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${isSel ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${isSel ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                              {isSel && <Check className="h-3 w-3 text-white" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-800 line-clamp-2">{riskDesc(r)}</p>
                              <p className="text-xs text-gray-400">
                                {riskLevel(r) && <span className="mr-2">{riskLevel(r)}</span>}
                                {cp || 'No cycle/process linked'}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected risks */}
              {selected.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Selected ({selected.length})</p>
                  <div className="space-y-1.5">
                    {selected.map(r => {
                      const cp = riskCycleProcess(r);
                      return (
                        <div key={riskKey(r)} className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 line-clamp-1">{riskDesc(r)}</p>
                            {cp && <p className="text-xs text-blue-600">{cp}</p>}
                          </div>
                          <button type="button" onClick={() => toggleRisk(r)} className="text-gray-400 hover:text-red-400">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Extra context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Context</label>
                <textarea
                  value={extra} onChange={e => setExtra(e.target.value)} rows={3}
                  maxLength={2000}
                  placeholder="e.g. performed in Workday; reviewer is the Finance Manager; threshold $50K…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-y"
                />
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}
            </>
          )}

          {/* Review */}
          {phase === 'review' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-1">Before</p>
                  <p className="text-sm text-gray-500 whitespace-pre-wrap">{currentWording || <span className="italic">(empty)</span>}</p>
                </div>
                <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600 mb-1">After (improved) — editable</p>
                  <textarea
                    value={improved}
                    onChange={e => setImproved(e.target.value)}
                    rows={6}
                    className="w-full text-sm text-gray-800 bg-white border border-green-200 rounded-md p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-y"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-2">
          {phase === 'input' && (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-2"
            >
              <Sparkles className="h-4 w-4" /> Generate
            </button>
          )}
          {phase === 'generating' && (
            <button type="button" disabled className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg opacity-70 inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating…
            </button>
          )}
          {phase === 'review' && (
            <>
              <button
                type="button"
                onClick={() => { onAccept(improved); onClose(); }}
                disabled={!(improved || '').trim()}
                className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Check className="h-4 w-4" /> Accept (populate field)
              </button>
              <button type="button" onClick={() => setPhase('input')} className="px-4 py-2 text-gray-600 border border-gray-300 text-sm rounded-lg hover:bg-gray-50">
                Back
              </button>
            </>
          )}
          <button type="button" onClick={onClose} className="ml-auto px-4 py-2 text-gray-500 text-sm hover:bg-gray-50 rounded-lg">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modal, document.body)
    : modal;
}
